import { ProductLiteResponseModel } from '../../modules/products-catalog/models/response-models/product-response.model';
import { ShipmentBoxModel } from '../../modules/warehouse-management/features/shipment-boxes/models/shipment-box.model';
import { CM_PER_INCH, KG_PER_POUND, M_PER_CM } from '../constants/unit.constants';
import { DimensionUnits, WeightUnits } from '../enums/enum';
import {
  BoxMetrics,
  DispatchBoxMetricsResult,
  DispatchBoxSummary,
  ProgressBarData,
  TransferDispatchBoxAllocationResponse,
} from '../features/outbound/models/transfer-dispatchbox-allocation.model';
import { TransferDispatchBoxResponse } from '../features/outbound/models/transfer-dispatchbox.model';

export function dimToMeters(value: number, unit: DimensionUnits): number {
  if (!value || value <= 0) return 0;

  switch (unit) {
    case DimensionUnits.CM:
      return value * M_PER_CM; // cm → m
    case DimensionUnits.Inch:
      return value * CM_PER_INCH * M_PER_CM; // inch → cm → m
    default:
      return value; // fallback, assume already in meters
  }
}

export function weightToKg(value: number, unit: WeightUnits): number {
  if (!value || value <= 0) return 0;

  switch (unit) {
    case WeightUnits.KG:
      return value;
    case WeightUnits.Pound:
      return value * KG_PER_POUND;
    default:
      return value;
  }
}
export function dimToCm(value: number, unit: DimensionUnits): number {
  if (!value || value <= 0) return 0;

  switch (unit) {
    case DimensionUnits.CM:
      return value;
    case DimensionUnits.Inch:
      return value * CM_PER_INCH; // inch → cm
    default:
      return value; // fallback assume already in cm
  }
}

// ✅ Weight conversion to Pounds
export function weightToPound(value: number, unit: WeightUnits): number {
  if (!value || value <= 0) return 0;
  switch (unit) {
    case WeightUnits.KG:
      return value / KG_PER_POUND; // kg → lb
    case WeightUnits.Pound:
      return value;
    default:
      return value; // fallback assume already in pounds
  }
}
// Assumes these exist in your codebase
// import { dimToMeters, weightToKg, weightToPound } from './conversions';
// import { DimensionUnits, WeightUnits } from './enums';
// import { DispatchBox, ShipmentBox, BoxAllocation, Product } from './models';

/**
 * Normalize allocation (ensures .Product exists and numeric qty/id)
 */
function normalizeAlloc(
  a: TransferDispatchBoxAllocationResponse,
): TransferDispatchBoxAllocationResponse {
  if (!a) return a;
  if (!a.product && (a as any).product) a.product = (a as any).product;
  a.quantityPicked = Number(a.quantityPicked ?? 0);
  a.id = Number(a.id ?? 0);
  return a;
}

/**
 * Returns per-unit volume in m^3 for a product. Falls back to boxDimUnit if product.dimensionUnit missing.
 */
function productVolumeM3(
  prod?: ProductLiteResponseModel,
  boxDimensionUnit?: DimensionUnits,
): number {
  if (!prod) return 0;
  const unit = (prod.dimensionUnit ?? boxDimensionUnit) as DimensionUnits;
  const h = dimToMeters(Number(prod.height ?? 0), unit);
  const w = dimToMeters(Number(prod.width ?? 0), unit);
  const l = dimToMeters(Number(prod.length ?? 0), unit);
  return h * w * l;
}

/**
 * Box volume in m^3 (uses shipmentBox.dimensionUnit)
 */
function boxVolumeM3(box: ShipmentBoxModel): number {
  const unit = box.dimensionUnit as DimensionUnits;
  const h = dimToMeters(Number(box.height ?? 0), unit);
  const w = dimToMeters(Number(box.width ?? 0), unit);
  const l = dimToMeters(Number(box.length ?? 0), unit);
  return h * w * l;
}

/**
 * Helper to create ProgressBarData for weight (kg)
 */
function makeWeightProgress(filledKg: number, capacityKg: number): ProgressBarData {
  const percent = capacityKg > 0 ? (filledKg / capacityKg) * 100 : 0;
  return {
    percent: Number(percent.toFixed(3)),
    value: Number(filledKg.toFixed(3)),
    max: Number(capacityKg.toFixed(3)),
    label: `${filledKg.toFixed(2)} / ${capacityKg.toFixed(2)} kg`,
  };
}

/**
 * Helper to create ProgressBarData for volume (m^3)
 */
function makeVolumeProgress(filledM3: number, capacityM3: number): ProgressBarData {
  const percent = capacityM3 > 0 ? (filledM3 / capacityM3) * 100 : 0;
  return {
    percent: Number(percent.toFixed(6)),
    value: Number(filledM3),
    max: Number(capacityM3),
    label: `${filledM3.toExponential(6)} / ${capacityM3.toExponential(6)} m³`,
  };
}

/**
 * Compute raw totals (kg and m^3) for a set of allocations and a shipment box.
 * includeNew: when true, includes allocations with id === 0. Otherwise only id > 0.
 */
function computeTotals(
  box: ShipmentBoxModel,
  allocations: TransferDispatchBoxAllocationResponse[],
  includeNew = false,
) {
  const normalized = (allocations ?? []).map(normalizeAlloc);
  const filtered = normalized.filter((a) => (includeNew ? true : a.id > 0));

  let totalKg = 0;
  let totalM3 = 0;

  for (const a of filtered) {
    const prod = a.product ?? ({} as ProductLiteResponseModel);
    const qty = Number(a.quantityPicked ?? 0);

    // product weight (may be in product.weightUnit or fallback to box.weightUnit)
    const pWeight = Number(prod.weight ?? 0);
    const pWeightUnit = (prod.weightUnit ?? box.weightUnit ?? WeightUnits.KG) as WeightUnits;
    const perUnitKg = weightToKg(pWeight, pWeightUnit);
    totalKg += perUnitKg * qty;

    // volume
    const perUnitM3 = productVolumeM3(prod, box.dimensionUnit);
    totalM3 += perUnitM3 * qty;
  }

  return { totalKg, totalM3 };
}

/**
 * Main exposed function: returns both current (id>0) and preview (include id===0) metrics for a DispatchBox.
 */
export function getDispatchBoxMetrics(
  dispatchBox: TransferDispatchBoxResponse,
): DispatchBoxMetricsResult {
  const box = dispatchBox.shipmentBox ?? new ShipmentBoxModel();
  const allocations = dispatchBox.transferDispatchBoxAllocations ?? [];

  // Current (only id>0)
  const { totalKg: currentKg, totalM3: currentM3 } = computeTotals(box, allocations, false);

  // Preview (including id===0)
  const { totalKg: previewKg, totalM3: previewM3 } = computeTotals(box, allocations, true);

  // Capacities
  const capacityKg = weightToKg(Number(box.maxWeight ?? 0), box.weightUnit ?? WeightUnits.KG);
  const capacityM3 = boxVolumeM3(box);

  const currentWeightProgress = makeWeightProgress(currentKg, capacityKg);
  const previewWeightProgress = makeWeightProgress(previewKg, capacityKg);

  const currentVolumeProgress = makeVolumeProgress(currentM3, capacityM3);
  const previewVolumeProgress = makeVolumeProgress(previewM3, capacityM3);

  // weight in pounds convenience
  const currentKgInLb = weightToPound(currentKg, WeightUnits.KG);
  const capacityKgInLb = weightToPound(capacityKg, WeightUnits.KG);
  const previewKgInLb = weightToPound(previewKg, WeightUnits.KG);

  const currentWeightLbProgress: ProgressBarData = {
    percent: currentWeightProgress.percent,
    value: Number(currentKgInLb.toFixed(3)),
    max: Number(capacityKgInLb.toFixed(3)),
    label: `${currentKgInLb.toFixed(2)} / ${capacityKgInLb.toFixed(2)} lb`,
  };

  const previewWeightLbProgress: ProgressBarData = {
    percent: previewWeightProgress.percent,
    value: Number(previewKgInLb.toFixed(3)),
    max: Number(capacityKgInLb.toFixed(3)),
    label: `${previewKgInLb.toFixed(2)} / ${capacityKgInLb.toFixed(2)} lb`,
  };

  const current: BoxMetrics = {
    weight: currentWeightProgress,
    weightLb: currentWeightLbProgress,
    volume: currentVolumeProgress,
    raw: {
      weightKg: Number(currentKg.toFixed(6)),
      capacityKg: Number(capacityKg.toFixed(6)),
      volumeM3: Number(currentM3),
      capacityM3: Number(capacityM3),
    },
  };

  const preview: BoxMetrics = {
    weight: previewWeightProgress,
    weightLb: previewWeightLbProgress,
    volume: previewVolumeProgress,
    raw: {
      weightKg: Number(previewKg.toFixed(6)),
      capacityKg: Number(capacityKg.toFixed(6)),
      volumeM3: Number(previewM3),
      capacityM3: Number(capacityM3),
    },
  };

  return { current, preview };
}
export function getDispatchBoxSummary(
  dispatchBox: TransferDispatchBoxResponse,
): DispatchBoxSummary {
  const box = dispatchBox.shipmentBox ?? {
    name: '',
    weightCapacity: 0,
    weightUnit: WeightUnits.KG,
  };
  const allocations = dispatchBox.transferDispatchBoxAllocations ?? [];

  const items = allocations.length;
  const units = allocations.reduce((sum, a) => sum + (a.quantityPicked || 0), 0);

  let weight = 0;

  const products = allocations.map((a) => {
    const productWeight = weightToKg(
      a.product?.weight ?? 0,
      a.product?.weightUnit ?? WeightUnits.KG,
    );
    const qty = a.quantityPicked || 0;
    const totalWeight = productWeight * qty;
    weight += totalWeight;

    return {
      productId: a.product?.id ?? 0,
      name: a.product?.name ?? '',
      sku: a.product?.sku ?? '',
      quantityPicked: qty,
      weight: Number(productWeight.toFixed(3)),
      totalWeight: Number(totalWeight.toFixed(3)),
    };
  });

  const weightCapacity = weightToKg(Number(box.maxWeight ?? 0), box.weightUnit ?? WeightUnits.KG);

  return {
    id: dispatchBox.id ?? 0,
    shipmentBoxName: box.name ?? '',
    boxName: dispatchBox.name ?? '',
    items,
    units,
    weight: Number(weight.toFixed(3)),
    weightCapacity: Number(weightCapacity.toFixed(3)),
    products,
  };
}

export function getDispatchBoxSummaries(
  dispatchBoxes: TransferDispatchBoxResponse[],
): DispatchBoxSummary[] {
  return dispatchBoxes.map((db) => getDispatchBoxSummary(db));
}
